import { ReactNode, useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { UserIcon } from '~/components/UserIcon'
import { useAuth } from '~/contexts/AuthContext'
import { useFriendRequests } from '~/hooks/useFriendRequests'
import { buildLoginRedirectPath } from '~/hooks/usePostAuthNavigation/utils'
import styles from './styles.module.css'

interface Props {
  children: ReactNode
  headerContent?: ReactNode
}

export function CommonLayout({ children, headerContent }: Props) {
  const { user, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { requests } = useFriendRequests()

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev)
  }, [])

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false)
  }, [])

  const handleShowProfile = useCallback(() => {
    navigate('/profile')
    setIsSidebarOpen(false)
  }, [navigate])

  // ゲストが常時サイドバーを見る画面（/worlds, /world/:id, /events）があるため、
  // ログイン後に元のページへ戻れるよう redirect を付ける
  const handleNavigateLogin = useCallback(() => {
    navigate(buildLoginRedirectPath(location.pathname + location.search))
    setIsSidebarOpen(false)
  }, [navigate, location.pathname, location.search])

  const handleNavigateHome = useCallback(() => {
    navigate('/')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateAvatar = useCallback(() => {
    navigate('/settings/avatar')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateWorlds = useCallback(() => {
    navigate('/worlds')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateEvents = useCallback(() => {
    navigate('/events')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateFriends = useCallback(() => {
    navigate('/friends')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateMarketplace = useCallback(() => {
    navigate('/marketplace')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateItems = useCallback(() => {
    navigate('/myitems')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateSharedFiles = useCallback(() => {
    navigate('/shared-files')
    setIsSidebarOpen(false)
  }, [navigate])

  const handleNavigateSettings = useCallback(() => {
    navigate('/settings')
    setIsSidebarOpen(false)
  }, [navigate])

  return (
    <div className={styles.commonLayout}>
      {/* モバイルヘッダー（ハンバーガーボタン + ロゴ） */}
      <div className={styles.mobileHeader}>
        <button
          className={styles.hamburgerButton}
          onClick={handleToggleSidebar}
          aria-label="メニューを開く"
        >
          <span className={styles.hamburgerIcon}>☰</span>
        </button>
        <h1 className={styles.mobileLogo}>
          <img src="/xrift.svg" alt="XRift" className={styles.logoImage} />
        </h1>
      </div>

      {/* 背景オーバーレイ（モバイルでサイドバー開いているとき） */}
      {isSidebarOpen && <div className={styles.overlay} onClick={handleCloseSidebar} />}

      <div className={`${styles.leftSidebar} ${isSidebarOpen ? styles.sidebarOpen : ''}`}>
        <h1 className={styles.logo}>
          <img src="/xrift.svg" alt="XRift" className={styles.logoImage} />
        </h1>
        {isLoading ? (
          <div className={styles.profileSkeleton} />
        ) : isAuthenticated ? (
          <button
            className={styles.profileButton}
            onClick={handleShowProfile}
            title={`${user?.displayName || 'ゲスト'}のプロフィール`}
          >
            <UserIcon
              userIconUrl={user?.userIconUrl}
              displayName={user?.displayName || 'ゲスト'}
              size="medium"
            />
            <span className={styles.profileName}>{user?.displayName || 'ゲスト'}</span>
          </button>
        ) : (
          <button className={styles.profileButton} onClick={handleNavigateLogin} title="ログイン">
            <span className={styles.profileName}>🔑 ログイン</span>
          </button>
        )}

        <nav className={styles.navigation}>
          <button
            className={styles.navButton}
            onClick={handleNavigateHome}
            title="インスタンス一覧"
          >
            🏠 ホーム
          </button>
          <button
            className={styles.navButton}
            onClick={handleNavigateMarketplace}
            title="マーケットプレイス"
          >
            🛒 マーケットプレイス
          </button>
          <button className={styles.navButton} onClick={handleNavigateWorlds} title="ワールド">
            🌐 ワールド
          </button>
          <button className={styles.navButton} onClick={handleNavigateEvents} title="イベント">
            📅 イベント
          </button>
          {isLoading ? (
            <>
              <div className={styles.navSkeleton} />
              <div className={styles.navSkeleton} />
              <div className={styles.navSkeleton} />
            </>
          ) : isAuthenticated ? (
            <>
              <button
                className={styles.navButton}
                onClick={handleNavigateItems}
                title="マイアイテム"
              >
                📦 マイアイテム
              </button>
              <button
                className={styles.navButton}
                onClick={handleNavigateSharedFiles}
                title="共有ファイル"
              >
                📁 共有ファイル
              </button>
              <button className={styles.navButton} onClick={handleNavigateAvatar} title="アバター">
                👤 アバター
              </button>
              <div className={styles.navButtonWrapper}>
                <button
                  className={styles.navButton}
                  onClick={handleNavigateFriends}
                  title="フレンド"
                >
                  👥 フレンド
                </button>
                {requests.length > 0 && <span className={styles.badge}>{requests.length}</span>}
              </div>
              <button className={styles.navButton} onClick={handleNavigateSettings} title="設定">
                ⚙️ 設定
              </button>
            </>
          ) : null}
        </nav>

        <div className={styles.externalLinks}>
          <a
            href="https://docs.xrift.net/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.externalLink}
            title="ドキュメント"
          >
            📖 ドキュメント
          </a>
        </div>

        <div className={styles.versionInfo}>
          <span className={styles.commitHash}>Version: {__COMMIT_HASH__}</span>
        </div>
      </div>

      <div className={styles.mainContent}>
        {headerContent && <div className={styles.headerArea}>{headerContent}</div>}
        <div className={styles.contentArea}>{children}</div>
        <footer className={styles.footer}>
          <a href="https://xrift.net/terms" target="_blank" rel="noopener noreferrer">
            利用規約
          </a>
          <span className={styles.footerSeparator}>|</span>
          <a href="https://xrift.net/privacy" target="_blank" rel="noopener noreferrer">
            プライバシーポリシー
          </a>
          <span className={styles.footerSeparator}>|</span>
          <a href="https://discord.gg/U5VtBjqekN" target="_blank" rel="noopener noreferrer">
            Discord
          </a>
          <span className={styles.footerSeparator}>|</span>
          <span>© {new Date().getFullYear()} XRift</span>
        </footer>
      </div>
    </div>
  )
}
